import { localStorageProvider } from "../../../../AppController";

export const CircadiaEngineProviderFactory: ICircadiaEngineProviderFactory = ({
  circadia: _circadiaInit,
}) => {
  let gColor1 = "";
  let gColor2 = "";

  // On Android, window.circadiaapp is injected asynchronously in onPageFinished,
  // AFTER AppController runs. _circadiaInit is therefore undefined at startup.
  // getBridge() re-reads window.circadiaapp at each call time so that once the
  // bridge script lands, all subsequent calls go to the real native bridge rather
  // than the web fallback stubs.
  const getBridge = (): ICircadiaEngine => {
    const live = (window as Window & { circadiaapp?: ICircadiaEngine })
      .circadiaapp;
    return live ?? _webFallback;
  };

  // Keep a module-level alias for code that uses `circadia` directly below.
  // This is intentionally a getter so it always resolves to the live bridge.
  let circadia: ICircadiaEngine = _circadiaInit!;

  const windowCircadiaStatus = !!_circadiaInit;
  // Re-check at call time — on Android the bridge script is injected
  // asynchronously (onPageFinished) so it may not be present at factory init.
  const isWindowCircadia = () => {
    if (windowCircadiaStatus) return true;
    return !!(window as Window & { circadiaapp?: unknown }).circadiaapp;
  };
  const isIOS = !!_circadiaInit && _circadiaInit.plat === "ios";

  let _webFallback: ICircadiaEngine;
  if (!_circadiaInit) {
    _webFallback = {
      saveConfig: async (key: string, value: string) => {
        return true;
      },
      createSSPin: async (jwt: string, pin: string) => {
        return true;
      },
      changeSSPin: async (jwt: string, pin: string) => {
        return true;
      },
      saveSSValue: async (email: string, key: string, value: string) => {
        const storeValue = {
          email,
          key,
          value,
        };
        try {
          localStorageProvider.set("saveSSvalue", JSON.stringify(storeValue));
          return true;
        } catch (error) {
          return Promise.reject();
        }
      },
      loadSSValue: async (email: string, key: string, value: string) => {
        const storeValue = {
          email,
          key,
          value,
        };
        const getSSvalue = JSON.parse(
          localStorageProvider.get("saveSSvalue") || ""
        );
        return storeValue.value === getSSvalue.value ? true : Promise.reject();
      },
      cognitoProvidePin: async (pin: string) => {
        return true;
      },
      setStyleSheet: async (styes: string) => {
        return true;
      },
      setNotifTokenCb: (cb: (token: string) => void) => {
        const token = "dummy-token";
        cb(token);
        return undefined;
      },
      loadSSValueBio: async (email: string, key: string) => {
        return false;
      },
      loginIntercomUser: async (
        token: string,
        hmac: string,
        userInfo: {
          name: string;
          facility: string;
          user_id?: string;
          email?: string;
        }
      ) => {
        let response = null;
        if (isWindowCircadia()) {
          // TODO: remove then or await
          await circadia
            .loginIntercomUser(token, hmac, userInfo)
            .then((res: string) => {
              response = { code: "success", message: res };
            })
            .catch((err: { message: string }) => {
              response = { code: "error", message: err.message };
            });
        }
        return response;
      },
      logoutIntercomUser: async () => {
        let response = null;
        if (isWindowCircadia()) {
          // TODO: remove then or await
          await circadia
            .logoutIntercomUser()
            .then((res: string) => {
              response = { code: "success", message: res };
            })
            .catch((err: { message: string }) => {
              response = { code: "error", message: err.message };
            });
        }
        return response;
      },
      showIntercom: async () => {
        let response = null;
        if (circadia) {
          await circadia
            .showIntercom()
            .then((res: boolean) => {
              response = { code: "success", message: res };
            })
            .catch((err: { message: string }) => {
              response = { code: "error", message: err.message };
            });
        }
        return response;
      },
      hideIntercom: async () => {
        let response = null;
        if (isWindowCircadia()) {
          await circadia
            .hideIntercom()
            .then((res: boolean) => {
              response = { code: "success", message: res };
            })
            .catch((err: { message: string }) => {
              response = { code: "error", message: err.message };
            });
        }
        return response;
      },
      cognitoProvideMfa: (mfa: string) => {},
      cognitoProvideSmsOTP: (code: string) => {},
      cognitoPhoneOTPLogin: (phone: string) => {},
      cognitoUserLogout: () => {},
      cognitoDeleteUser: () => Promise.reject("Not available in web mode"),
      setCognitoLoginFailedCb: (
        cb: (reason: { Code: string; Msg: string }) => void
      ) => {},
      setCognitoLoginDoneCb: (cb: () => void) => {},
      setCognitoMfaRequiredCb: (
        cb: (reason: {
          CODE_DELIVERY_DELIVERY_MEDIUM: string;
          CODE_DELIVERY_DESTINATION: string;
        }) => void
      ) => {},
      setCognitoPinRequiredCb: (cb: () => void) => {},
      setCognitoSmsOTPRequiredCb: (
        cb: (data: { destination?: string }) => void
      ) => {},
      cognitoTokens: async () => {
        return { AccessToken: "", IdToken: "" };
      },
      cognitoImportWebSession: async () => {
        throw new Error("Web session import requires the native app.");
      },
      playSound: (sound: string) => {},
      cognitoUserLogin: (username: string, password: string) => {},
      cognitoProvideNewPassword: (password: string, attrs?: unknown) => {},
      setCognitoNewPasswordRequiredCb: (cb: (attrs: unknown) => void) => {},
      plat: "web",
      cognitoChangePassword: (
        accessToken: string,
        prevPassword: string,
        newPassword: string
      ) => {
        return { mdg: "", code: 0 };
      },
      intfv: -1,
      scanDeviceId: async () => {
        throw new Error("scanDeviceId is not available in the browser");
      },
      requestNotifPermission: async () => ({ granted: false }),
      scheduleLocalNotification: async () => {},
      cancelLocalNotification: async () => {},
      getPendingNotificationIds: async () => ({ ids: [] as number[] }),
      getCurrentSsid: async () => ({ ssid: null }),
      saveWifiCredentials: async () => {},
      getWifiCredentials: async () => ({ ssid: null, password: null }),
      clearWifiCredentials: async () => {},
      cognitoUserSignUp: async () => {
        throw new Error("Self-signup requires the native app.");
      },
      cognitoConfirmSignUp: async () => {
        throw new Error("Self-signup requires the native app.");
      },
      cognitoResendConfirmationCode: async () => {
        throw new Error("Self-signup requires the native app.");
      },
      // ── VoIP (native only) ──
      voipStartCall: async () => {
        throw new Error("VoIP requires the native app.");
      },
      voipEndCall: async () => {},
      voipStartTalking: async () => {
        throw new Error("VoIP requires the native app.");
      },
      voipStopTalking: async () => {},
      voipSetVolume: async () => {},
      voipSetAudioRoute: async () => {},
      voipSetRxGain: async () => {},
      setVoipStateCb: () => {},
      setVoipStatusCb: () => {},
      // ── Device AI assistant (native only) ──
      aiAssistant: async () => {
        throw new Error("AI assistant requires the native app.");
      },
    } as unknown as ICircadiaEngine;
  } else {
    _webFallback = _circadiaInit;
  }
  // Route every bridge call through getBridge() so Android's late-arriving
  // window.circadiaapp is used rather than the startup-time stubs.
  // Using _webFallback as the Proxy target (not {}) keeps the own-enumerable
  // properties intact, so `return { ...circadia }` still spreads all methods.
  circadia = new Proxy(_webFallback, {
    get(_t, prop) {
      const bridge = getBridge();
      const val = (bridge as any)[prop];
      // Bind to bridge so `this` is correct inside bridge methods.
      return typeof val === "function" ? val.bind(bridge) : val;
    },
  });

  const playSound = async (sound: string) => {
    if (isWindowCircadia()) {
      return circadia.playSound(sound);
    }
    return true;
  };

  const getDeviceToken = async () =>
    new Promise<string>((resolve) => {
      if (!circadia) {
        return resolve("");
      }

      circadia.setNotifTokenCb((token) => resolve(token));
      return "";
    });

  const changeAppTopAndBottom = (color1: string, color2: string) => {
    if (color1 === gColor1 && color2 === gColor2) return;
    gColor1 = color1;
    gColor2 = color2;

    if (!circadia) {
      return;
    }
    circadia.setStyleSheet(
      `background-color:qlineargradient(spread:pad, x1:0, y1:0, x2:0, y2:1, stop:0.000 ${color1},stop:0.250 ${color1} ,stop:0.750 ${color2}, stop:1.000 ${color2});`
    );
  };

  const loginIntercomUser = async (
    token: string,
    hmac: string,
    userInfo: {
      name: string;
      facility: string;
      user_id?: string;
      email?: string;
    }
  ) => {
    let response = null;
    if (isWindowCircadia()) {
      await circadia
        .loginIntercomUser(token, hmac, userInfo)
        .then((res: string) => {
          response = { code: "success", message: res };
        })
        .catch((err: { message: string }) => {
          response = { code: "error", message: err.message };
        });
    }
    return response;
  };

  const logoutIntercomUser = async () => {
    let response = null;
    if (isWindowCircadia()) {
      await circadia
        .logoutIntercomUser()
        .then((res: string) => {
          response = { code: "success", message: res };
        })
        .catch((err: { message: string }) => {
          response = { code: "error", message: err.message };
        });
    }
    return response;
  };
  const showIntercom = async () => {
    let response = null;
    if (isWindowCircadia()) {
      await circadia
        .showIntercom()
        .then((res: boolean) => {
          response = { code: "success", message: res };
        })
        .catch((err: { message: string }) => {
          response = { code: "error", message: err.message };
        });
    }
    return response;
  };
  const hideIntercom = async () => {
    let response = null;
    if (isWindowCircadia()) {
      await circadia
        .hideIntercom()
        .then((res: boolean) => {
          response = { code: "success", message: res };
        })
        .catch((err: { message: string }) => {
          response = { code: "error", message: err.message };
        });
    }
    return response;
  };

  const scanDeviceId = async (
    strings?: Record<string, string>
  ): Promise<{ deviceId: string; deviceType?: string }> => {
    return circadia.scanDeviceId(strings);
  };

  const scheduleLocalNotification = (
    opts: Parameters<ICircadiaEngine["scheduleLocalNotification"]>[0]
  ): Promise<void> => {
    return circadia.scheduleLocalNotification(opts);
  };

  const cancelLocalNotification = (opts: { id: number }): Promise<void> => {
    return circadia.cancelLocalNotification(opts);
  };

  const getPendingNotificationIds = (): Promise<{ ids: number[] }> => {
    return circadia.getPendingNotificationIds();
  };

  const getCurrentSsid = (): Promise<{ ssid: string | null }> => {
    return circadia.getCurrentSsid();
  };

  const saveWifiCredentials = (options: {
    ssid: string;
    password: string;
  }): Promise<void> => {
    return circadia.saveWifiCredentials(options);
  };

  const getWifiCredentials = (): Promise<{
    ssid: string | null;
    password: string | null;
  }> => {
    return circadia.getWifiCredentials();
  };

  const clearWifiCredentials = (): Promise<void> => {
    return circadia.clearWifiCredentials();
  };

  // ── Self-signup bridge methods ─────────────────────────────────────────

  const cognitoUserSignUp = (
    email: string,
    password: string,
    firstName: string,
    lastName: string,
    birthdate: string,
    gender: string,
    phone?: string
  ) => {
    return circadia.cognitoUserSignUp(
      email,
      password,
      firstName,
      lastName,
      birthdate,
      gender,
      phone
    );
  };

  const cognitoConfirmSignUp = (email: string, code: string) => {
    return circadia.cognitoConfirmSignUp(email, code);
  };

  const cognitoResendConfirmationCode = (email: string) => {
    return circadia.cognitoResendConfirmationCode(email);
  };

  /**
   * Route through the Proxy (not the init-time spread) so Android's
   * late-arriving bridge is honored. Imports a web (Amplify) session into the
   * native side so a user who logged in via the web fallback stays signed in
   * natively. Native only — the web fallback rejects.
   */
  const cognitoImportWebSession = (options: {
    clientId: string;
    username?: string;
    refreshToken: string;
    deviceKey?: string;
    deviceGroupKey?: string;
  }) => {
    return circadia.cognitoImportWebSession(options);
  };

  /**
   * True only when the live native bridge actually exposes the three
   * Cognito self-signup methods. We read the live `window.circadiaapp`
   * directly rather than going through the Proxy, because the web
   * fallback defines these methods (they throw "Self-signup requires
   * the native app.") and a `typeof === 'function'` check on the Proxy
   * would otherwise return true in the browser. Web shells / older
   * native builds without these handlers therefore correctly resolve to
   * `false`, which the GetStarted screen uses to hide the sign-up CTA.
   */
  const isSelfSignupSupported = (): boolean => {
    if (!isWindowCircadia()) return false;
    const live = (window as Window & { circadiaapp?: ICircadiaEngine })
      .circadiaapp;
    if (!live) return false;
    return (
      typeof (live as Partial<ICircadiaEngine>).cognitoUserSignUp ===
        "function" &&
      typeof (live as Partial<ICircadiaEngine>).cognitoConfirmSignUp ===
        "function" &&
      typeof (live as Partial<ICircadiaEngine>)
        .cognitoResendConfirmationCode === "function"
    );
  };

  /**
   * Invoke `window.circadiaapp.getAppVersion()` lazily through the Proxy so
   * Android's late-arriving bridge is honored (vs. a snapshot at factory
   * init time). Validates the payload shape (`{ versionName, versionCode }`)
   * and returns undefined for the web fallback, when the method isn't
   * shipped yet, when the call throws, or when the payload is malformed.
   */
  const getNativeAppVersion = async (): Promise<
    { versionName: string; versionCode: string | number } | undefined
  > => {
    if (!isWindowCircadia()) return undefined;
    const fn = (circadia as { getAppVersion?: () => unknown }).getAppVersion;
    if (typeof fn !== "function") return undefined;
    try {
      const raw = (await fn()) as
        | { versionName?: unknown; versionCode?: unknown }
        | null
        | undefined;
      if (!raw || typeof raw !== "object") return undefined;
      const versionName =
        typeof raw.versionName === "string" && raw.versionName.trim()
          ? raw.versionName.trim()
          : undefined;
      const versionCode =
        typeof raw.versionCode === "string" ||
        typeof raw.versionCode === "number"
          ? raw.versionCode
          : undefined;
      if (!versionName || versionCode === undefined) return undefined;
      return { versionName, versionCode };
    } catch {
      return undefined;
    }
  };

  return {
    ...circadia,
    changeAppTopAndBottom,
    getDeviceToken,
    hideIntercom,
    showIntercom,
    logoutIntercomUser,
    loginIntercomUser,
    isWindowCircadia,
    playSound,
    scanDeviceId,
    scheduleLocalNotification,
    cancelLocalNotification,
    getPendingNotificationIds,
    getCurrentSsid,
    saveWifiCredentials,
    getWifiCredentials,
    clearWifiCredentials,
    isIOS,
    cognitoUserSignUp,
    cognitoConfirmSignUp,
    cognitoResendConfirmationCode,
    cognitoImportWebSession,
    isSelfSignupSupported,
    getNativeAppVersion,
  };
};
